/*
 * anointed-unified-cart.js - ONE shared cart across the Liquid store, every Mart Club store and AnimalDander.
 *
 * The Anointed API owns the shared cart (api/storefront-cart). This store keeps Shopify's native cart as the
 * cart the shopper sees and edits here (drawer, cart page, discount codes, express checkout), and keeps it in
 * step with the shared cart both ways:
 *   - every native cart change (add, change, update, clear) is pushed as a DELTA (POST/PATCH/DELETE lines);
 *   - lines this store added on another device, or a restored cart, are pulled into the native cart;
 *   - lines added on the other stores are never pulled; the drawer and cart page show "N more items in your
 *     cart from other shops" with links;
 *   - the checkout buttons open ONE draft order checkout for the whole shared cart (POST /checkout) when it
 *     holds items from another shop (or always, with checkoutMode "always"), else the native checkout runs;
 *   - guests get an "email me my cart" box, and /cart/restore?token= (the link in that email) restores;
 *   - a guest clicking through to Mart Club or AnimalDander carries the cart: a single-use handoff token
 *     (POST /guest/handoff) rides along as ?aa_cart=, and an inbound ?aa_cart= is claimed (POST /guest/claim)
 *     then stripped from the URL. Any failure is a plain link / an ignored param.
 * Every API failure falls back to the native cart untouched: this script must never cost a sale.
 * Config: window.AAUnifiedCartConfig, rendered by snippets/anointed-unified-cart.liquid.
 */
(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else if (root && root.document) {
    api.boot(root);
  }
}(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  var VARIANT_GID = 'gid://shopify/ProductVariant/';
  var GUEST_KEY = 'aaUnifiedCartGuest';
  var GUEST_COOKIE = 'aa_cart_guest';
  var SNAP_KEY = 'aaUnifiedCartSnap';
  var FOLD_KEY = 'aaUnifiedCartFolded';
  var PARTNER_KEY = 'aaPartnerAttribution';
  var PARTNER_COOKIE = 'aa_partner';
  var PARTNER_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
  var GUEST_MAX_AGE_S = 30 * 24 * 60 * 60;
  var MAX_QTY = 99;
  var SLUG_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var RESTORE_PATHS = ['/cart/restore', '/pages/cart-restore'];
  var HANDOFF_PARAM = 'aa_cart';
  var HANDOFF_HOSTS = ['www.mart.club', 'mart.club', 'www.animaldander.com', 'animaldander.com'];
  var CHECKOUT_SELECTOR ='#mui-checkout-btn, #cart-drawer-checkout-btn, #checkout, button[name="checkout"], input[name="checkout"]';

  // ---------- pure helpers (unit tested in anointed-unified-cart.test.js) ----------

  // Reads a field whatever its casing (the API is PascalCase for reference types, camelCase for value types).
  function g(obj, key) {
    if (!obj || typeof obj !== 'object') { return undefined; }
    if (obj[key] !== undefined) { return obj[key]; }
    var lower = key.charAt(0).toLowerCase() + key.slice(1);
    if (obj[lower] !== undefined) { return obj[lower]; }
    var upper = key.charAt(0).toUpperCase() + key.slice(1);
    return obj[upper];
  }

  function variantNumber(id) {
    if (id === null || id === undefined) { return null; }
    var s = String(id);
    if (s.indexOf(VARIANT_GID) === 0) { s = s.slice(VARIANT_GID.length); }
    return /^[1-9][0-9]*$/.test(s) ? s : null;
  }

  function variantGid(id) {
    var n = variantNumber(id);
    return n ? VARIANT_GID + n : null;
  }

  function normalizeSlug(value) {
    if (typeof value !== 'string') { return null; }
    var s = value.trim().toLowerCase();
    return SLUG_RE.test(s) ? s : null;
  }

  // The live partner slug from the partner-attribution record ({slug, at}), or null.
  function livePartner(rec, nowMs) {
    if (!rec || typeof rec.at !== 'number' || isNaN(rec.at)) { return null; }
    if (rec.at > nowMs + 5 * 60 * 1000 || nowMs - rec.at >= PARTNER_WINDOW_MS) { return null; }
    return normalizeSlug(rec.slug);
  }

  // Native Shopify cart (/cart.js) -> { variantNumber: { qty, mart, keys:[{key, qty}] } }.
  function nativeMap(cart) {
    var out = {};
    var items = (cart && cart.items) ? cart.items : [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var v = variantNumber(it.variant_id);
      if (!v) { continue; }
      if (!out[v]) { out[v] = { qty: 0, mart: null, keys: [] }; }
      out[v].qty += it.quantity || 0;
      out[v].keys.push({ key: it.key, qty: it.quantity || 0 });
      var props = it.properties || {};
      if (!out[v].mart && normalizeSlug(props.mart)) { out[v].mart = normalizeSlug(props.mart); }
    }
    return out;
  }

  // Shared cart view -> { variantNumber: { qty, source } }.
  function unifiedMap(view) {
    var out = {};
    var lines = g(view, 'Lines') || [];
    for (var i = 0; i < lines.length; i++) {
      var v = variantNumber(g(lines[i], 'VariantId'));
      if (!v) { continue; }
      if (!out[v]) { out[v] = { qty: 0, source: String(g(lines[i], 'SourceStore') || '') }; }
      out[v].qty += g(lines[i], 'quantity') || 0;
    }
    return out;
  }

  /*
   * Two-way sync plan. snapshot = { v: { n, u } } as of the last sync (native and shared quantities).
   * A native change since the snapshot is pushed to the shared cart as a delta (local wins); otherwise a
   * shared change on a line THIS store owns is pulled into the native cart. With no snapshot entry the
   * line is only seeded one way (native-only -> push, shared-only liquid line -> pull), never doubled.
   * Returns { unified: [{op:'add'|'set'|'remove', v, qty, mart}], native: [{v, target, mart}] }.
   */
  function planSync(native, unified, snapshot, store, partnerSlug) {
    var plan = { unified: [], native: [] };
    var seen = {};
    var ids = [];
    var k;
    for (k in native) { if (Object.prototype.hasOwnProperty.call(native, k) && !seen[k]) { seen[k] = 1; ids.push(k); } }
    for (k in unified) { if (Object.prototype.hasOwnProperty.call(unified, k) && !seen[k]) { seen[k] = 1; ids.push(k); } }
    snapshot = snapshot || {};
    for (var i = 0; i < ids.length; i++) {
      var v = ids[i];
      var n = native[v] ? native[v].qty : 0;
      var u = unified[v] ? unified[v].qty : 0;
      var ours = !!unified[v] && unified[v].source === store;
      var mart = (native[v] && native[v].mart) || partnerSlug || null;
      var s = snapshot[v];
      if (!s) {
        if (n > 0 && u === 0) {
          plan.unified.push({ op: 'add', v: v, qty: Math.min(n, MAX_QTY), mart: mart });
        } else if (n === 0 && u > 0 && ours) {
          plan.native.push({ v: v, target: u, mart: null });
        }
        continue;
      }
      var dn = n - (s.n || 0);
      var du = u - (s.u || 0);
      if (dn > 0) {
        plan.unified.push({ op: 'add', v: v, qty: Math.min(dn, MAX_QTY), mart: mart });
      } else if (dn < 0) {
        var target = Math.max(0, u + dn);
        plan.unified.push(target === 0 ? { op: 'remove', v: v, qty: 0, mart: null } : { op: 'set', v: v, qty: Math.min(target, MAX_QTY), mart: null });
      } else if (du !== 0 && (ours || (u === 0 && n > 0))) {
        plan.native.push({ v: v, target: Math.max(0, n + du), mart: null });
      }
    }
    return plan;
  }

  // The snapshot after a sync: every variant either side still holds.
  function buildSnapshot(native, unified) {
    var snap = {};
    var k;
    for (k in native) { if (Object.prototype.hasOwnProperty.call(native, k)) { snap[k] = { n: native[k].qty, u: 0 }; } }
    for (k in unified) {
      if (Object.prototype.hasOwnProperty.call(unified, k)) {
        if (!snap[k]) { snap[k] = { n: 0, u: 0 }; }
        snap[k].u = unified[k].qty;
      }
    }
    return snap;
  }

  // Native line updates (by line key) that move a variant's total from its current sum to `target`.
  function nativeUpdates(entry, target) {
    var updates = {};
    var keys = entry.keys;
    var diff = target - entry.qty;
    if (target <= 0) {
      for (var i = 0; i < keys.length; i++) { updates[keys[i].key] = 0; }
      return updates;
    }
    if (diff > 0) {
      updates[keys[0].key] = keys[0].qty + diff;
      return updates;
    }
    for (var j = keys.length - 1; j >= 0 && diff < 0; j--) {
      var cut = Math.min(keys[j].qty, -diff);
      updates[keys[j].key] = keys[j].qty - cut;
      diff += cut;
    }
    return updates;
  }

  function storeLabel(key) {
    var k = String(key || '').toLowerCase();
    if (k === 'liquid') { return 'Anointed'; }
    if (k === 'hub') { return 'Mart Club'; }
    if (k === 'animaldander') { return 'AnimalDander'; }
    return k + '.mart.club';
  }

  // OtherStores -> [{ label, count, url }] for the notice (https links only).
  function otherStores(view) {
    var list = g(view, 'OtherStores') || [];
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var count = g(list[i], 'itemCount') || 0;
      var url = String(g(list[i], 'Url') || '');
      if (count <= 0) { continue; }
      out.push({ label: storeLabel(g(list[i], 'SourceStore')), count: count, url: /^https:\/\//.test(url) ? url : null });
    }
    return out;
  }

  function otherCount(view) {
    var list = otherStores(view);
    var total = 0;
    for (var i = 0; i < list.length; i++) { total += list[i].count; }
    return total;
  }

  function isEmail(value) {
    return typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
  }

  function restoreToken(pathname, search) {
    var path = String(pathname || '').replace(/\/+$/, '').toLowerCase();
    if (RESTORE_PATHS.indexOf(path) < 0) { return null; }
    var q = String(search || '').replace(/^\?/, '').split('&');
    for (var i = 0; i < q.length; i++) {
      var kv = q[i].split('=');
      if (decodeURIComponent(kv[0] || '') === 'token' && kv[1]) {
        return decodeURIComponent(kv[1].replace(/\+/g, ' '));
      }
    }
    return '';
  }

  // True for the other properties that share this cart (Mart Club stores and AnimalDander).
  function isHandoffHost(host) {
    var h = String(host || '').toLowerCase().replace(/\.$/, '');
    if (HANDOFF_HOSTS.indexOf(h) >= 0) { return true; }
    return /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.mart\.club$/.test(h);
  }

  // The absolute https/http URL of a link to another property, or null (same site, other site, mailto...).
  function handoffTarget(href, base) {
    try {
      var u = new URL(String(href || ''), base);
      if (u.protocol !== 'https:' && u.protocol !== 'http:') { return null; }
      return isHandoffHost(u.hostname) ? u.href : null;
    } catch (e) { return null; }
  }

  // url with aa_cart=<token> (replacing any old one), fragment kept.
  function withCartParam(url, token) {
    var u = new URL(url);
    u.searchParams.delete(HANDOFF_PARAM);
    u.searchParams.set(HANDOFF_PARAM, token);
    return u.href;
  }

  // The aa_cart value from a query string, or null.
  function cartParam(search) {
    var q = String(search || '').replace(/^\?/, '').split('&');
    for (var i = 0; i < q.length; i++) {
      var kv = q[i].split('=');
      if (decodeURIComponent(kv[0] || '') === HANDOFF_PARAM && kv[1]) {
        return decodeURIComponent(kv[1].replace(/\+/g, ' '));
      }
    }
    return null;
  }

  // href without aa_cart (other params and the fragment kept).
  function stripCartParam(href) {
    var u = new URL(href);
    u.searchParams.delete(HANDOFF_PARAM);
    return u.href;
  }

  // ---------- browser ----------

  function boot(W) {
    var D = W.document;
    var CFG = W.AAUnifiedCartConfig || {};
    var API = typeof CFG.apiBase === 'string' ? CFG.apiBase.replace(/\/+$/, '') : '';
    var STORE = CFG.store || 'liquid';
    var CUSTOMER_ID = CFG.customerId ? String(CFG.customerId) : null;
    var SIGNATURE = CFG.signature || null;
    var CUSTOMER_EMAIL = CFG.customerEmail || null;
    var MODE = CFG.checkoutMode === 'always' ? 'always' : 'cross-store';
    if (!API || !W.fetch || !W.Promise) { return; }

    var state = {
      healthy: true,
      coolUntil: 0,
      view: null,
      viewAt: 0,
      syncing: null,
      again: false,
      self: 0,
      bypass: false,
      timer: null
    };

    function dbg() {
      try { W.console.debug.apply(W.console, ['[aa-cart]'].concat([].slice.call(arguments))); } catch (e) { /* silent */ }
    }

    // ----- storage -----

    function lsGet(key) { try { return W.localStorage.getItem(key); } catch (e) { return null; } }
    function lsSet(key, value) { try { W.localStorage.setItem(key, value); } catch (e) { /* storage blocked */ } }
    function lsDel(key) { try { W.localStorage.removeItem(key); } catch (e) { /* storage blocked */ } }
    function ssGet(key) { try { return W.sessionStorage.getItem(key); } catch (e) { return null; } }
    function ssSet(key, value) { try { W.sessionStorage.setItem(key, value); } catch (e) { /* storage blocked */ } }

    function readCookie(name) {
      try {
        var m = D.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return m ? decodeURIComponent(m[1]) : null;
      } catch (e) { return null; }
    }

    function guestToken() {
      return lsGet(GUEST_KEY) || readCookie(GUEST_COOKIE) || null;
    }

    function setGuestToken(token) {
      if (!token || typeof token !== 'string') { return; }
      lsSet(GUEST_KEY, token);
      try {
        D.cookie = GUEST_COOKIE + '=' + encodeURIComponent(token) + '; Max-Age=' + GUEST_MAX_AGE_S + '; Path=/; SameSite=Lax; Secure';
      } catch (e) { /* no cookies */ }
    }

    function clearGuestToken() {
      lsDel(GUEST_KEY);
      try { D.cookie = GUEST_COOKIE + '=; Max-Age=0; Path=/; SameSite=Lax; Secure'; } catch (e) { /* no cookies */ }
    }

    function ownerKey() {
      if (CUSTOMER_ID) { return 'c:' + CUSTOMER_ID; }
      var t = guestToken();
      return t ? 'g:' + t.split('.')[0] : null;
    }

    function readSnapshot() {
      var key = ownerKey();
      if (!key) { return null; }
      try {
        var all = JSON.parse(lsGet(SNAP_KEY) || 'null');
        return all && all.owner === key ? all.lines : null;
      } catch (e) { return null; }
    }

    function writeSnapshot(lines) {
      var key = ownerKey();
      if (key) { lsSet(SNAP_KEY, JSON.stringify({ owner: key, lines: lines })); }
    }

    function partnerSlug() {
      var now = Date.now();
      try {
        var rec = JSON.parse(lsGet(PARTNER_KEY) || 'null');
        var slug = livePartner(rec, now);
        if (slug) { return slug; }
      } catch (e) { /* bad record */ }
      var raw = readCookie(PARTNER_COOKIE);
      if (raw) {
        var parts = raw.split('|');
        return livePartner({ slug: parts[0], at: parseInt(parts[1], 10) }, now);
      }
      return null;
    }

    // ----- API -----

    function usable() { return state.healthy || Date.now() >= state.coolUntil; }

    function call(method, path, body, opts) {
      opts = opts || {};
      if (!opts.force && !usable()) { return Promise.reject(new Error('cart api cooling down')); }
      var qs = 'store=' + encodeURIComponent(STORE);
      if (CUSTOMER_ID && SIGNATURE) {
        qs += '&customerId=' + encodeURIComponent(CUSTOMER_ID) + '&signature=' + encodeURIComponent(SIGNATURE);
      }
      var headers = { Accept: 'application/json' };
      if (body !== undefined) { headers['Content-Type'] = 'application/json'; }
      var token = guestToken();
      if (token && (!CUSTOMER_ID || opts.withGuest)) { headers['X-Cart-Guest-Token'] = token; }
      var ctrl = W.AbortController ? new W.AbortController() : null;
      var timer = ctrl ? W.setTimeout(function () { ctrl.abort(); }, opts.timeout || 8000) : null;
      var url = API + '/api/storefront-cart' + path + (path.indexOf('?') >= 0 ? '&' : '?') + qs;
      return W.fetch(url, {
        method: method,
        headers: headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        credentials: 'omit',
        mode: 'cors',
        signal: ctrl ? ctrl.signal : undefined
      }).then(function (r) {
        if (timer) { W.clearTimeout(timer); }
        return r.json().catch(function () { return null; }).then(function (env) {
          var data = g(env, 'Data');
          if (r.ok) {
            state.healthy = true;
            var fresh = g(data, 'GuestToken');
            if (fresh && (!CUSTOMER_ID || opts.adoptGuest)) { setGuestToken(fresh); }
            return data;
          }
          var err = new Error(String(g(env, 'Message') || ('HTTP ' + r.status)));
          err.status = r.status;
          if (r.status === 429 || r.status >= 500) {
            state.healthy = false;
            state.coolUntil = Date.now() + (r.status === 429 ? 60000 : 30000);
          }
          if (r.status === 401 && token && !CUSTOMER_ID && !opts.retried) {
            // The guest token expired or was rotated: start a fresh guest cart once.
            clearGuestToken();
            return mintGuest().then(function () {
              opts.retried = true;
              return call(method, path, body, opts);
            });
          }
          throw err;
        });
      }, function (e) {
        if (timer) { W.clearTimeout(timer); }
        state.healthy = false;
        state.coolUntil = Date.now() + 30000;
        throw e;
      });
    }

    function mintGuest() {
      return call('POST', '/guest', {}, { force: true }).then(function (data) {
        var token = g(data, 'GuestToken');
        if (!token) { throw new Error('no guest token'); }
        setGuestToken(token);
        return token;
      });
    }

    function keepView(view) {
      if (view && g(view, 'Lines')) {
        state.view = view;
        state.viewAt = Date.now();
      }
      return view;
    }

    function getView(maxAgeMs) {
      if (state.view && Date.now() - state.viewAt < (maxAgeMs || 0)) { return Promise.resolve(state.view); }
      return call('GET', '').then(keepView);
    }

    // ----- native cart -----

    function nativeJson(path, body) {
      state.self++;
      return W.fetch(path, {
        method: body ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: body ? JSON.stringify(body) : undefined
      }).then(function (r) {
        return r.json().then(function (j) {
          if (!r.ok) { var e = new Error((j && j.description) || ('cart ' + r.status)); e.status = r.status; throw e; }
          return j;
        });
      }).then(function (j) { state.self--; return j; }, function (e) { state.self--; throw e; });
    }

    function readNative() { return nativeJson('/cart.js'); }

    function announceNative(cart) {
      if (/^\/cart\/?$/.test(W.location.pathname)) { W.location.reload(); return; }
      try {
        D.dispatchEvent(new W.CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, sourceId: 'anointed-unified-cart', data: { itemCount: cart.item_count, source: 'anointed-unified-cart' } }
        }));
      } catch (e) { /* old browser */ }
    }

    // ----- sync -----

    function applyUnified(ops) {
      var chain = Promise.resolve(null);
      ops.forEach(function (op) {
        chain = chain.then(function () {
          var gid = variantGid(op.v);
          var req;
          if (op.op === 'add') {
            var body = { VariantId: gid, quantity: op.qty, SourceStore: STORE, Attributes: {} };
            if (op.mart) { body.Attributes.mart = op.mart; }
            req = call('POST', '/lines', body);
          } else if (op.op === 'set') {
            req = call('PATCH', '/lines', { VariantId: gid, quantity: op.qty });
          } else {
            req = call('DELETE', '/lines?variantId=' + encodeURIComponent(gid));
          }
          return req.then(keepView, function (e) {
            if (e.status === 400 || e.status === 404) { dbg('line skipped', op, e.message); return null; }
            throw e;
          });
        });
      });
      return chain;
    }

    function applyNative(ops, native) {
      if (!ops.length) { return Promise.resolve(false); }
      var updates = {};
      var adds = [];
      ops.forEach(function (op) {
        if (native[op.v]) {
          var u = nativeUpdates(native[op.v], op.target);
          for (var key in u) { if (Object.prototype.hasOwnProperty.call(u, key)) { updates[key] = u[key]; } }
        } else if (op.target > 0) {
          adds.push({ id: parseInt(op.v, 10), quantity: op.target });
        }
      });
      var chain = Promise.resolve();
      if (Object.keys(updates).length) {
        chain = chain.then(function () { return nativeJson('/cart/update.js', { updates: updates }); });
      }
      adds.forEach(function (item) {
        // One add per item so an unavailable variant never blocks the rest.
        chain = chain.then(function () {
          return nativeJson('/cart/add.js', { items: [item] }).catch(function (e) { dbg('native add skipped', item.id, e.message); });
        });
      });
      return chain.then(function () { return true; });
    }

    function runSync() {
      return readNative().then(function (cart) {
        var native = nativeMap(cart);
        var hasNative = cart && cart.item_count > 0;
        var ready = CUSTOMER_ID ? Promise.resolve() : (guestToken() ? Promise.resolve() : (hasNative ? mintGuest() : null));
        if (ready === null) { render(null); return null; }
        return ready.then(function () { return getView(1500); }).then(function (view) {
          var unified = unifiedMap(view);
          var plan = planSync(native, unified, readSnapshot(), STORE, partnerSlug());
          return applyUnified(plan.unified).then(function () {
            return applyNative(plan.native, native);
          }).then(function (nativeChanged) {
            var after = nativeChanged ? readNative() : Promise.resolve(cart);
            return after.then(function (finalCart) {
              writeSnapshot(buildSnapshot(nativeMap(finalCart), unifiedMap(state.view)));
              if (nativeChanged) { announceNative(finalCart); }
              render(state.view);
              return state.view;
            });
          });
        });
      }).catch(function (e) {
        dbg('sync failed, native cart stays in charge', e && e.message);
        render(null);
        return null;
      });
    }

    function sync() {
      if (state.syncing) { state.again = true; return state.syncing; }
      state.syncing = runSync().then(function (v) {
        state.syncing = null;
        if (state.again) { state.again = false; return sync(); }
        return v;
      });
      return state.syncing;
    }

    function scheduleSync() {
      if (state.timer) { W.clearTimeout(state.timer); }
      state.timer = W.setTimeout(sync, 500);
    }

    function hookFetch() {
      var prev = W.fetch;
      W.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var p = prev.apply(this, arguments);
        if (state.self === 0 && CART_MUTATION_RE.test(url)) {
          p.then(scheduleSync, function () { /* native error: nothing changed */ });
        }
        return p;
      };
    }

    // ----- UI -----

    function injectStyles() {
      if (D.getElementById('aa-ucart-style')) { return; }
      var css = ''
        + '.aa-ucart{font-family:inherit;font-size:14px;line-height:1.4;border:1px solid rgba(0,0,0,.12);border-radius:8px;padding:12px 14px;margin:10px 0;background:rgba(124,77,255,.06);color:inherit;box-sizing:border-box;width:100%;min-width:0;max-width:100%;overflow-wrap:anywhere}'
        + '.aa-ucart a{color:#7c4dff;font-weight:600;text-decoration:underline}'
        + '.aa-ucart__title{font-weight:700;margin:0 0 4px}'
        + '.aa-ucart__list{margin:4px 0 0;padding:0;list-style:none;display:flex;flex-wrap:wrap;gap:6px 14px}'
        + '.aa-ucart__note{margin:6px 0 0;font-size:13px;opacity:.8}'
        + '.aa-ucart__row{display:flex;gap:8px;margin-top:8px}'
        + '.aa-ucart__row input{flex:1 1 auto;width:0;min-width:0;padding:10px 12px;border:1px solid rgba(0,0,0,.2);border-radius:6px;font-size:16px;background:#fff;color:#000}'
        + '.aa-ucart__row button{padding:10px 14px;border:0;border-radius:6px;background:#000;color:#fff;font-weight:600;cursor:pointer;white-space:nowrap}'
        + '.aa-ucart__row button[disabled]{opacity:.6;cursor:default}'
        + '.aa-ucart__msg{margin:6px 0 0;font-size:13px}'
        + '.aa-ucart__msg--ok{color:#2e7d32}.aa-ucart__msg--err{color:#c62828}'
        + '.aa-ucart-restore{max-width:560px;margin:60px auto;padding:24px 16px;text-align:center}'
        + 'html.dark-mode .aa-ucart{border-color:rgba(255,255,255,.2)}';
      var el = D.createElement('style');
      el.id = 'aa-ucart-style';
      el.textContent = css;
      D.head.appendChild(el);
    }

    function el(tag, cls, text) {
      var n = D.createElement(tag);
      if (cls) { n.className = cls; }
      if (text) { n.textContent = text; }
      return n;
    }

    function othersBox(view) {
      var stores = otherStores(view);
      var total = otherCount(view);
      if (!total) { return null; }
      var box = el('div', 'aa-ucart aa-ucart--others');
      box.setAttribute('data-aa-ucart', 'others');
      box.appendChild(el('p', 'aa-ucart__title', total + ' more item' + (total === 1 ? '' : 's') + ' in your cart from other shops'));
      var ul = el('ul', 'aa-ucart__list');
      stores.forEach(function (s) {
        var li = el('li');
        if (s.url) {
          var a = el('a', null, s.label + ' (' + s.count + ')');
          a.href = s.url;
          li.appendChild(a);
        } else {
          li.textContent = s.label + ' (' + s.count + ')';
        }
        ul.appendChild(li);
      });
      box.appendChild(ul);
      box.appendChild(el('p', 'aa-ucart__note', 'Checking out here buys everything in ONE order, so your bulk discount counts every item.'));
      return box;
    }

    function prefillEmail() {
      var ids = ['mui-cart-recovery-email', 'cart-drawer-email'];
      for (var i = 0; i < ids.length; i++) {
        var input = D.getElementById(ids[i]);
        if (input && isEmail(input.value)) { return input.value.trim(); }
      }
      return '';
    }

    var emailMemo = { sent: false, msg: '', ok: false };

    function emailBox() {
      var box = el('div', 'aa-ucart aa-ucart--email');
      box.setAttribute('data-aa-ucart', 'email');
      box.appendChild(el('p', 'aa-ucart__title', 'Not ready yet?'));
      box.appendChild(el('p', 'aa-ucart__note', "I'll email you a link that brings this cart back on any device."));
      var row = el('div', 'aa-ucart__row');
      var input = el('input');
      input.type = 'email';
      input.autocomplete = 'email';
      input.placeholder = 'Your email';
      input.setAttribute('aria-label', 'Email me my cart');
      input.value = prefillEmail();
      var btn = el('button', null, emailMemo.sent ? 'Sent' : 'Email me my cart');
      btn.type = 'button';
      var msg = el('p', 'aa-ucart__msg' + (emailMemo.msg ? (emailMemo.ok ? ' aa-ucart__msg--ok' : ' aa-ucart__msg--err') : ''), emailMemo.msg);
      btn.addEventListener('click', function () {
        var email = input.value.trim();
        if (!isEmail(email)) {
          msg.className = 'aa-ucart__msg aa-ucart__msg--err';
          msg.textContent = 'That email does not look right.';
          input.focus();
          return;
        }
        btn.disabled = true;
        btn.textContent = 'Sending...';
        call('POST', '/email', { Email: email }).then(function (data) {
          var sent = !!g(data, 'sent');
          emailMemo.sent = sent;
          emailMemo.ok = sent;
          emailMemo.msg = sent ? 'Sent! Check your inbox for the link.' : (String(g(data, 'Reason') || "I couldn't send that one. Try a different email."));
        }, function (e) {
          emailMemo.ok = false;
          emailMemo.msg = e && e.status === 429
            ? 'Easy there, give it a minute and try again.'
            : "That didn't go through. Give it a minute and try again.";
        }).then(function () {
          btn.disabled = emailMemo.sent;
          btn.textContent = emailMemo.sent ? 'Sent' : 'Email me my cart';
          msg.className = 'aa-ucart__msg ' + (emailMemo.ok ? 'aa-ucart__msg--ok' : 'aa-ucart__msg--err');
          msg.textContent = emailMemo.msg;
        });
      });
      if (emailMemo.sent) { btn.disabled = true; }
      row.appendChild(input);
      row.appendChild(btn);
      box.appendChild(row);
      box.appendChild(msg);
      return box;
    }

    // Where each block goes: [container, insert-before child or null].
    function mounts() {
      var out = [];
      var drawerTop = D.querySelector('cart-drawer-component .cart-drawer__top-checkout');
      var drawerContent = D.querySelector('cart-drawer-component .cart-drawer__content');
      if (drawerTop) {
        out.push({ name: 'drawer', parent: drawerTop, before: drawerTop.firstChild });
      } else if (drawerContent) {
        out.push({ name: 'drawer', parent: drawerContent, before: null });
      }
      var summary = D.querySelector('.mui-cart__summary-card');
      var empty = D.querySelector('.mui-cart__empty');
      if (summary) {
        out.push({ name: 'page', parent: summary, before: D.getElementById('mui-checkout-btn') });
      } else if (empty) {
        out.push({ name: 'page', parent: empty, before: null });
      }
      return out;
    }

    var lastRendered = null;

    function render(view) {
      lastRendered = view;
      var old = D.querySelectorAll('[data-aa-ucart]');
      for (var i = 0; i < old.length; i++) { old[i].parentNode.removeChild(old[i]); }
      if (!view || !state.healthy) { return; }
      injectStyles();
      var itemCount = g(view, 'itemCount') || 0;
      var guest = !CUSTOMER_ID;
      mounts().forEach(function (m) {
        var frag = D.createDocumentFragment();
        var others = othersBox(view);
        if (others) { frag.appendChild(others); }
        if (guest && itemCount > 0) { frag.appendChild(emailBox()); }
        if (frag.childNodes.length) { m.parent.insertBefore(frag, m.before && m.before.parentNode === m.parent ? m.before : null); }
      });
    }

    function watchMounts() {
      var pending = false;
      try {
        new W.MutationObserver(function () {
          if (pending || !lastRendered) { return; }
          pending = true;
          W.setTimeout(function () {
            pending = false;
            var want = mounts();
            var missing = false;
            want.forEach(function (m) { if (!m.parent.querySelector('[data-aa-ucart]')) { missing = true; } });
            if (missing && (otherCount(lastRendered) > 0 || (!CUSTOMER_ID && (g(lastRendered, 'itemCount') || 0) > 0))) {
              render(lastRendered);
            }
          }, 150);
        }).observe(D.body, { childList: true, subtree: true });
      } catch (e) { /* old browser */ }
    }

    // ----- checkout -----

    function wantsUnifiedCheckout() {
      if (state.bypass || !state.healthy || !state.view) { return false; }
      return MODE === 'always' ? (g(state.view, 'itemCount') || 0) > 0 : otherCount(state.view) > 0;
    }

    function checkoutEmail() {
      if (CUSTOMER_EMAIL && isEmail(CUSTOMER_EMAIL)) { return CUSTOMER_EMAIL; }
      var e = prefillEmail();
      return e || null;
    }

    function nativeCheckout(btn) {
      state.bypass = true;
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      btn.click();
    }

    function hookCheckout() {
      D.addEventListener('click', function (ev) {
        var btn = ev.target && ev.target.closest ? ev.target.closest(CHECKOUT_SELECTOR) : null;
        if (!btn || btn.disabled || !wantsUnifiedCheckout()) { return; }
        ev.preventDefault();
        ev.stopImmediatePropagation();
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        var body = {};
        var email = checkoutEmail();
        var slug = partnerSlug();
        if (email) { body.Email = email; }
        if (slug) { body.PartnerSlug = slug; }
        sync().then(function () {
          if (!wantsUnifiedCheckout()) { throw new Error('shared cart unavailable'); }
          return call('POST', '/checkout', body, { timeout: 15000 });
        }).then(function (data) {
          var url = String(g(data, 'CheckoutUrl') || '');
          if (!/^https:\/\//.test(url)) { throw new Error('no checkout url'); }
          W.location.href = url;
        }).catch(function (e) {
          dbg('shared checkout failed, using the native checkout', e && e.message);
          nativeCheckout(btn);
        });
      }, true);
    }

    // ----- cross-domain guest handoff -----

    var handoffBusy = false;

    // A guest leaving for Mart Club or AnimalDander carries the cart: a single-use handoff token rides along as
    // ?aa_cart= and the other site claims it. Signed-in customers and guests with no cart go as plain links.
    function onOutboundClick(ev) {
      if (ev.defaultPrevented || CUSTOMER_ID || !guestToken()) { return; }
      if (ev.button !== 0 && ev.button !== 1) { return; }
      if (ev.altKey) { return; }
      var a = ev.target && ev.target.closest ? ev.target.closest('a[href]') : null;
      if (!a || a.hasAttribute('download')) { return; }
      var target = handoffTarget(a.getAttribute('href'), W.location.href);
      if (!target) { return; }
      var newTab = ev.button === 1 || ev.ctrlKey || ev.metaKey || ev.shiftKey || (a.target && a.target !== '_self' && a.target !== '_top' && a.target !== '_parent');
      ev.preventDefault();
      if (handoffBusy) { return; }
      handoffBusy = true;
      function go(url) {
        handoffBusy = false;
        if (newTab) {
          var w = W.open(url, '_blank');
          if (w) { try { w.opener = null; } catch (e) { /* cross origin */ } return; }
        }
        W.location.href = url;
      }
      call('POST', '/guest/handoff', undefined, { timeout: 4000, retried: true }).then(function (data) {
        var token = g(data, 'HandoffToken');
        if (!token || typeof token !== 'string') { throw new Error('no handoff token'); }
        go(withCartParam(target, token));
      }).catch(function (e) {
        dbg('cart handoff failed, plain link', e && e.message);
        go(target);
      });
    }

    function hookOutbound() {
      D.addEventListener('click', onOutboundClick, true);
      D.addEventListener('auxclick', function (ev) { if (ev.button === 1) { onOutboundClick(ev); } }, true);
    }

    // ?aa_cart= from another property: claim it (adopt or merge), then the normal sync pulls this store's lines.
    function claimIfNeeded() {
      var handoff = cartParam(W.location.search);
      if (handoff === null) { return Promise.resolve(); }
      try { W.history.replaceState(W.history.state, '', stripCartParam(W.location.href)); } catch (e) { /* old browser */ }
      return call('POST', '/guest/claim', { HandoffToken: handoff }, { force: true, timeout: 10000 }).then(function (view) {
        var fresh = g(view, 'GuestToken');
        if (fresh && !CUSTOMER_ID) { setGuestToken(fresh); }
        keepView(view);
      }, function (e) {
        // 404: expired, used or unknown. Anything else: the cart on this device stays as it is.
        dbg('cart handoff claim skipped', e && e.status, e && e.message);
      });
    }

    // ----- restore + fold -----

    function restore(token) {
      var panel = D.getElementById('aa-ucart-restore');
      var main = D.getElementById('MainContent');
      if (panel && main && panel.parentNode !== main) { main.insertBefore(panel, main.firstChild); }
      D.documentElement.classList.add('aa-ucart-restoring');
      var status = D.getElementById('aa-ucart-restore-status');
      function say(text) { if (status) { status.textContent = text; } }
      if (!token) { say("That cart link is missing its code. Your cart is still here though."); return; }
      call('POST', '/restore', { Token: token }, { force: true, adoptGuest: true, timeout: 12000 })
        .then(function (view) {
          keepView(view);
          return runSync();
        })
        .then(function () { W.location.replace('/cart'); })
        .catch(function (e) {
          say(e && e.status === 404
            ? 'This cart link expired or was already used. Anything you added on this device is still in your cart.'
            : "I couldn't load that cart right now. Give it a minute and try the link again.");
        });
    }

    function foldIfNeeded() {
      var token = guestToken();
      if (!CUSTOMER_ID || !SIGNATURE || !token || ssGet(FOLD_KEY) === CUSTOMER_ID) { return Promise.resolve(); }
      return call('POST', '/fold', undefined, { withGuest: true }).then(function (view) {
        keepView(view);
        ssSet(FOLD_KEY, CUSTOMER_ID);
        clearGuestToken();
      }, function (e) {
        // 400/401: the guest cart is gone or the token is dead; nothing to fold.
        if (e && (e.status === 400 || e.status === 401)) { ssSet(FOLD_KEY, CUSTOMER_ID); clearGuestToken(); }
      });
    }

    function start() {
      var token = restoreToken(W.location.pathname, W.location.search);
      hookFetch();
      if (token !== null) { restore(token); return; }
      hookCheckout();
      hookOutbound();
      watchMounts();
      foldIfNeeded().then(claimIfNeeded).then(sync);
    }

    W.AAUnifiedCart = { sync: sync, state: state };
    if (D.readyState === 'loading') { D.addEventListener('DOMContentLoaded', start); } else { start(); }
  }

  return {
    boot: boot,
    g: g,
    variantNumber: variantNumber,
    variantGid: variantGid,
    normalizeSlug: normalizeSlug,
    livePartner: livePartner,
    nativeMap: nativeMap,
    unifiedMap: unifiedMap,
    planSync: planSync,
    buildSnapshot: buildSnapshot,
    nativeUpdates: nativeUpdates,
    otherStores: otherStores,
    otherCount: otherCount,
    storeLabel: storeLabel,
    isEmail: isEmail,
    restoreToken: restoreToken,
    isHandoffHost: isHandoffHost,
    handoffTarget: handoffTarget,
    withCartParam: withCartParam,
    cartParam: cartParam,
    stripCartParam: stripCartParam
  };
}));
